import React from "react";
import { cn } from "../../lib/utils";
import PropTypes from "prop-types";

/**
 * SidebarItem - Individual navigation item for sidebar
 */
export function SidebarItem({
  children,
  icon,
  label,
  isActive = false,
  onClick,
  href,
  className = "",
  iconClassName = "",
  labelClassName = "",
  badge,
  disabled = false,
}) {
  const Component = href ? "a" : "button";
  
  return (
    <Component
      href={href}
      onClick={disabled ? undefined : onClick}
      className={cn(
        "w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-primary-3 focus:ring-offset-2",
        isActive
          ? "bg-primary-8 text-primary-3 border border-primary-4"
          : "text-neutral-8 hover:bg-neutral-3 hover:text-neutral-12",
        disabled && "opacity-50 cursor-not-allowed pointer-events-none",
        className
      )}
      disabled={disabled}
    >
      {icon && (
        <span className={cn("flex-shrink-0", iconClassName)}>
          {icon}
        </span>
      )}
      
      <span className={cn("flex-1 text-left", labelClassName)}>
        {label || children}
      </span>
      
      {badge && (
        <span className="flex-shrink-0">
          {badge}
        </span>
      )}
    </Component>
  );
}
SidebarItem.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.node,
  label: PropTypes.string,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  href: PropTypes.string,
  className: PropTypes.string,
  iconClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  badge: PropTypes.node,
  disabled: PropTypes.bool,
};

/**
 * SidebarSection - Grouped section with optional header
 */
export function SidebarSection({
  children,
  title,
  className = "",
  titleClassName = "",
}) {
  return (
    <div className={cn("mb-6", className)}>
      {title && (
        <h3
          className={cn(
            "px-4 mb-3 text-xs font-semibold text-neutral-7 uppercase tracking-wide",
            titleClassName
          )}
        >
          {title}
        </h3>
      )}
      <nav className="space-y-1">
        {children}
      </nav>
    </div>
  );
}
SidebarSection.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
};

/**
 * SidebarDivider - Visual separator between sections
 */
export function SidebarDivider({ className = "" }) {
  return (
    <hr className={cn("my-4 border-neutral-5", className)} />
  );
}
SidebarDivider.propTypes = {
  className: PropTypes.string,
};

/**
 * Sidebar - Main sidebar container with built-in sections
 */
export function Sidebar({
  children,
  header,
  footer,
  className = "",
  headerClassName = "",
  footerClassName = "",
  contentClassName = "",
}) {
  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Sidebar Header */}
      {header && (
        <div className={cn("px-4 py-6 border-b border-neutral-5", headerClassName)}>
          {header}
        </div>
      )}
      
      {/* Sidebar Content */}
      <div className={cn("flex-1 py-6 overflow-y-auto", contentClassName)}>
        {children}
      </div>
      
      {/* Sidebar Footer */}
      {footer && (
        <div className={cn("px-4 py-6 border-t border-neutral-5", footerClassName)}>
          {footer}
        </div>
      )}
    </div>
  );
}
Sidebar.propTypes = {
  children: PropTypes.node,
  header: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
};

/**
 * Pre-built navigation sidebar with common patterns
 */
export function NavigationSidebar({
  navigationItems = [],
  sections = [],
  header,
  footer,
  className = "",
  onItemClick,
  activeItem,
}) {
  return (
    <Sidebar
      header={header}
      footer={footer}
      className={className}
    >
      {/* Direct navigation items */}
      {navigationItems.length > 0 && (
        <SidebarSection>
          {navigationItems.map((item, index) => (
            <SidebarItem
              key={item.id || index}
              icon={item.icon}
              label={item.label}
              isActive={activeItem === item.id}
              onClick={() => onItemClick?.(item)}
              href={item.href}
              badge={item.badge}
              disabled={item.disabled}
            />
          ))}
        </SidebarSection>
      )}
      
      {/* Organized sections */}
      {sections.map((section, sectionIndex) => (
        <React.Fragment key={section.id || sectionIndex}>
          <SidebarSection title={section.title}>
            {section.items.map((item, itemIndex) => (
              <SidebarItem
                key={item.id || itemIndex}
                icon={item.icon}
                label={item.label}
                isActive={activeItem === item.id}
                onClick={() => onItemClick?.(item)}
                href={item.href}
                badge={item.badge}
                disabled={item.disabled}
              />
            ))}
          </SidebarSection>
          
          {/* Add divider between sections except for the last one */}
          {sectionIndex < sections.length - 1 && <SidebarDivider />}
        </React.Fragment>
      ))}
    </Sidebar>
  );
}
NavigationSidebar.propTypes = {
  navigationItems: PropTypes.arrayOf(PropTypes.object),
  sections: PropTypes.arrayOf(PropTypes.object),
  header: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
  onItemClick: PropTypes.func,
  activeItem: PropTypes.string,
};


/**
 * Collapsible sidebar item with sub-items
 */
export function CollapsibleSidebarItem({
  children,
  icon,
  label,
  isActive = false,
  isExpanded = false,
  onToggle,
  className = "",
  badge,
}) {
  return (
    <div className={cn("", className)}>
      <SidebarItem
        icon={icon}
        label={label}
        isActive={isActive}
        onClick={onToggle}
        className="justify-between"
        badge={
          <div className="flex items-center space-x-2">
            {badge}
            <svg
              className={cn(
                "w-4 h-4 transition-transform",
                isExpanded ? "rotate-90" : "rotate-0"
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        }
      />
      
      {isExpanded && (
        <div className="ml-6 mt-1 space-y-1">
          {children}
        </div>
      )}
    </div>
  );
}
CollapsibleSidebarItem.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.node,
  label: PropTypes.string,
  isActive: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  className: PropTypes.string,
  badge: PropTypes.node,
};